import { UniButton } from '@/design-system/components/buttons';
import { UniIconButton } from '@/design-system/components/buttons';
import { Divider } from '@/design-system/components/Divider';
import { Icon } from '@/design-system/components/icons/Icon';
import { UniSwitch } from '@/design-system/components/inputs';
import { Slider } from '@/design-system/components/slider/Slider';
import { H5, TextBody, TextCaption, TextTitle } from '@/design-system/components/typography';
import { SxProp, View, useDripsyTheme, styled } from 'dripsy';
import _, { debounce } from 'lodash';
import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { thousandSeparator } from '@/utils/numberFormatting';
import { useNavigation } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { SetCardLimitStackParamList, SetCardLimitStackRoutes } from './type';
import { PageHeaderModalNative } from '../page/PageHeaderModalNative';
import { UniInputAmount } from '@/design-system/components/inputs';
import { CardWithLimitProp } from '@/features/account-management/types';
import { CardLimitSubtitle, CardLimitTitle } from '../../options';
import { CardLimitType } from '../../enum';

export interface SetCardLimitProps {
  onClose: () => void;
  onLayout: (event: any) => void;
  dailyLimitType: string | undefined;
  onData?: (data: any) => void;
  account: string;
  tab?: number;
  record?: CardWithLimitProp;
  func?: (
    cardId: string,
    atmLimit?: number,
    purchaseLimit?: number,
    onSuccess?: (data: any) => void,
    onError?: (error: any) => void
  ) => void;
  onReturnData?: (data: any) => void;
  refresh?: () => void;
}

export const DailyPurchaseLimitNativeScreen: React.FC<SetCardLimitProps> = (props) => {
  const { onClose, onLayout, dailyLimitType, onData, account, tab, record, func, onReturnData, refresh } = props;
  const { t } = useTranslation('account-management');

  const initialValue = () => {
    switch (dailyLimitType) {
      case CardLimitType.DailyPurchaseLimit:
        return record?.dailyPurchaseLimit;
      case CardLimitType.AtmLimit:
        return record?.dailyAtmLimit;
      case CardLimitType.DailyLimit:
        return record?.dailyLimit;
      case CardLimitType.SingleTransactionLimit:
        return record?.singleTransactionLimit;
      default:
        return null;
    }
  };

  const [value, setValue] = useState<number>(initialValue() ?? record?.cardLimit ?? 0);
  const [maxValue, setMaxValue] = useState<number>(record?.limit || 0);
  const [isEnabled, setIsEnabled] = useState(initialValue() == null);

  const { theme } = useDripsyTheme();

  const navigation = useNavigation<NativeStackNavigationProp<SetCardLimitStackParamList>>();

  const debounceValue = useCallback((value) => setInputValue(value), []);
  const setInputValue = debounce((value) => {
    setValue(value), 300;
  });

  return (
    <View>
      <PageHeaderModalNative>
        <View sx={{ px: '$4', mt: '$5', mb: '$4' }}>
          <View sx={{ position: 'absolute', top: 0, left: 12, zIndex: 2 }}>
            <UniIconButton
              icon="arrowLeft"
              options={{ iconStyles: { color: theme?.colors?.$white as string } }}
              onPress={() => navigation.popToTop()}></UniIconButton>
          </View>
          <View sx={{ textAlign: 'center', width: '100%' }}>
            <TextTitle bold sx={{ color: theme?.colors?.$white as string }}>
              {CardLimitTitle(t).find((e) => e.value == dailyLimitType)?.label}
            </TextTitle>
          </View>
        </View>
      </PageHeaderModalNative>
      <View sx={styles.modal}>
        <TextTitle sx={styles.textMb} semiBold>
          {CardLimitSubtitle(t).find((e) => e.value == dailyLimitType)?.label}
        </TextTitle>
        <View sx={styles.flexContainer}>
          <View sx={styles.textCaption}>
            <View sx={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
              <TextCaption semiBold>{t('card-account-management::set-card-limit.text--use-entire-secured-credit-limit')}</TextCaption>
              <UniSwitch
                value={isEnabled}
                onValueChange={(value) => {
                  setIsEnabled(value);
                }}
              />
            </View>
            <View sx={{ mt: '$4', mb: '$3', alignItems: 'center' }}>
              <Divider width={300} sx={{ backgroundColor: '$borderField' }} />
            </View>
            <TextCaption>{t('card-account-management::set-card-limit.text--choose-entire-secured-limit')}</TextCaption>
            <TextCaption semiBold sx={{ color: '#F5423E' }}>
              {t('card-account-management::set-card-limit.text--please-use-with-caution')}
            </TextCaption>
          </View>
        </View>
        {!isEnabled && (
          <>
            <View sx={styles.dailyLimitBox}>
              <View sx={{ ...styles.limit, alignItems: 'flex-end' }}>
                <TextBody sx={{ pb: '$2' }}>{record?.currency || 'HKD'}</TextBody>
                <UniInputAmount
                  sx={{ width: '42%' }}
                  assetPrefix={false}
                  maxFlag={false}
                  editable={!isEnabled}
                  sxWrap={{ border: 0 }}
                  sxInput={{
                    fontWeight: 'bolder',
                    fontSize: '$5',
                    color: isEnabled ? '$navbarTextInactive' : '$secondary',
                    ml: '$2',
                    pl: 0
                  }}
                  asset={''}
                  value={value}
                  max={maxValue}
                  onValueChange={(v) => v && debounceValue(v)}
                />
              </View>
              <Divider width={250} sx={{ margin: 'auto', backgroundColor: '$secondary' }} />
              <TextCaption semiBold>{t('card-account-management::set-card-limit.text--daily-limit')}</TextCaption>
            </View>
            <Slider
              value={value >= maxValue ? maxValue : value}
              minValue={0}
              maxValue={maxValue}
              step={100}
              labelFormatter={(value) => ''}
              onValueChange={setValue}
              disabled={isEnabled}
            />
            <View sx={styles.flex}>
              <TextBody semiBold>0 {record?.currency || 'HKD'}</TextBody>
              <TextBody semiBold>
                {thousandSeparator({ value: maxValue })} {record?.currency || 'HKD'}
              </TextBody>
            </View>
          </>
        )}
        {dailyLimitType == 'ATMWithdrawalLimit' && (
          <View sx={styles.noteContainer}>
            <TextBody semiBold sx={styles.title}>
              {t('card-account-management::set-card-limit.text--note')}
            </TextBody>
            <TextBody sx={styles.title}>{t('card-account-management::set-card-limit.text--service-fee-per-transaction')}</TextBody>
          </View>
        )}
        <View sx={styles.buttonBox}>
          <UniButton
            size="medium"
            sx={{ width: '100%', height: 44 }}
            labelSx={{ color: '$balck' }}
            label={t('card-account-management::set-card-limit.button--set-limit')}
            onPress={() => {
              onData && onData({ value, isEnabled });
              navigation.navigate(SetCardLimitStackRoutes.Confirmation, {
                onLayout: onLayout,
                data: { value, isEnabled },
                dailyLimitType: dailyLimitType,
                account,
                tab,
                record,
                func,
                onReturnData,
                refresh
              });
            }}
          />
        </View>
      </View>
    </View>
  );
};

const styles: Record<string, SxProp> = {
  modal: {
    px: '$4',
    py: '$5'
  },
  topHeader: {
    justifyContent: 'center'
  },
  textMb: {
    marginBottom: 20
  },
  flexContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    backgroundColor: '$backgroundSecondary',
    borderRadius: 10,
    padding: 16
  },
  textCaption: {
    width: '100%',
    gap: 4
  },
  amount: {
    color: '$secondary',
    ml: '$2'
  },
  amountDisabled: {
    color: '$navbarTextInactive',
    ml: '$2'
  },
  limit: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center'
  },
  dailyLimitBox: {
    alignItems: 'center',
    py: '$5',
    mt: '$5',
    gap: 4
  },
  flex: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  buttonBox: {
    width: '92%',
    position: 'fixed',
    left: 0,
    bottom: 0,
    marginX: 15,
    marginBottom: 30
  },
  noteContainer: {
    mt: '$5',
    color: '$textSecondary',
    gap: 4
  },
  title: {
    color: '$textSecondary',
    fontSize: 12
  }
};
